import { Pipe, PipeTransform } from '@angular/core';
import { Request, isInboundTraceSource } from '../models/tracer.model';

// Status semantics — one canonical scale used everywhere we render
// request-or-session status (left rail tiles on /search, the request
// card pill at the top of /search, the session-row tint in the home
// table, the per-request rows under an expanded session).
//
// Colours follow the user-approved mock:
//
//   pill — small inline badge, stronger contrast
//   tile — full-row/full-tile tint, soft contrast
//
//   ok       → white            / green pill
//   warning  → soft amber       / amber pill
//   error    → soft red         / red pill
//   not_set  → soft gray        / gray pill
//
// Selection state (black ring) is orthogonal and applied separately by
// the consumer via [ngClass]="selected ? 'ring-2 ring-gray-900 …' : ''".
export type StatusLabel = 'ok' | 'warning' | 'error' | 'not_set';

export interface StatusInfo {
  label: StatusLabel;
  /** Inline pill badge (high-contrast). */
  pill: string;
  /** Tile / row background tint (soft). Includes hover state. */
  tile: string;
}

@Pipe({
  name: 'getStatus',
})
export class GetStatusPipe implements PipeTransform {
  transform(request: Request | undefined | null): StatusInfo {
    if (!request) {
      return STATUS.not_set;
    }

    // Authoritative: server-computed aggregate (requests.aggregate_status,
    // migration 66). Walking request.traces here would systematically
    // disagree with sessions.status when the agent role projection in
    // GetSession filters out non-inbound traces — yellow session pill,
    // every per-request row green. The BE recomputes both from the full
    // trace set on every write so the two can never diverge.
    if (request.aggregate_status) {
      switch (request.aggregate_status) {
        case 'ok':
          return STATUS.ok;
        case 'warning':
          return STATUS.warning;
        case 'error':
          return STATUS.error;
        case 'not_set':
          return STATUS.not_set;
      }
    }

    // Legacy walk — kept so FE deployed ahead of the BE migration still
    // renders correctly. Drops away once every backend rolls past
    // migration 66.
    if (!request.traces?.length) {
      return STATUS.not_set;
    }

    const jsonApiTraces = request.traces.filter(t => isInboundTraceSource(t?.source));
    const requesterTraces = request.traces.filter(t => t?.source === 'requester');

    // Hard error: the inbound (json-api / sidecar) leg failed. The
    // request as a whole was a failure regardless of what individual
    // provider calls did.
    const jsonApiHasError = jsonApiTraces.some(t => t?.status?.toLowerCase() === 'error');
    if (jsonApiHasError) {
      return STATUS.error;
    }

    // Hard error: every outbound (requester) leg errored. Even if
    // json-api itself returned 200 (e.g. AGW returned a fallback or a
    // last-known good cache), no provider succeeded — count it as a
    // failure.
    const allRequesterError =
      requesterTraces.length > 0 &&
      requesterTraces.every(t => t?.status?.toLowerCase() === 'error');
    if (allRequesterError) {
      return STATUS.error;
    }

    // Warning: some outbound legs failed but at least one succeeded
    // (or was retried successfully). Captures partial fan-out failures
    // — a request that "succeeded" but where individual provider calls
    // were lost. Operators care about these even when the surface
    // status is OK, but they're not full failures.
    //
    // EXCEPT for AirShopping. AirShopping is a multi-provider fan-out
    // where the user-visible outcome is "I got offers" — the consumer
    // is happy as long as at least one airline returned results. A
    // partial fan-out failure here is operationally invisible to the
    // consumer (they got offers from the airlines that responded;
    // the ones that didn't are silently absent), so flagging it as a
    // warning produces a flood of yellow rows that don't actually
    // represent a problem worth investigating. For AirShopping we
    // collapse to the all-or-nothing rule: ok unless every airline
    // failed (which is already the 'error' case above).
    const isAirShopping = request.method?.toLowerCase() === 'airshopping';
    if (!isAirShopping) {
      const anyRequesterError = requesterTraces.some(t => t?.status?.toLowerCase() === 'error');
      if (anyRequesterError) {
        return STATUS.warning;
      }
    }

    const hasOk = request.traces.some(t => t?.status?.toLowerCase() === 'ok');
    if (hasOk) {
      return STATUS.ok;
    }

    return STATUS.not_set;
  }
}

// Exported so the session-aggregate pipe can pick a status entry by
// label without redefining the colour mapping.
export const STATUS: Record<StatusLabel, StatusInfo> = {
  ok: {
    label: 'ok',
    pill: 'bg-green-100 text-green-800',
    tile: 'bg-white border-gray-200 hover:bg-gray-50',
  },
  warning: {
    label: 'warning',
    pill: 'bg-amber-100 text-amber-800',
    tile: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
  },
  error: {
    label: 'error',
    pill: 'bg-red-100 text-red-800',
    tile: 'bg-red-50 border-red-200 hover:bg-red-100',
  },
  not_set: {
    label: 'not_set',
    pill: 'bg-gray-100 text-gray-800',
    tile: 'bg-gray-50 border-gray-200 hover:bg-gray-100',
  },
};
